/**
 * P2-5 阶段 SOP 引导卡片
 *
 * 两种使用场景：
 *   1. compact 模式（sales dashboard）：单个 stage tab + 概要
 *   2. detail 模式（customer detail）：单 stage 全展开 + tailored 个性化提示
 *
 * 后端 GET /api/sales-sop?stage=L3&customerId=123 返回 { sop, tailored }
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { $ } from '../lib/api';
import { HomeCard, Pill } from './ui';

const STAGES = [
  { key: 'L1', label: 'L1 触达' },
  { key: 'L2', label: 'L2 破冰' },
  { key: 'L3', label: 'L3 挖需' },
  { key: 'L4', label: 'L4 深聊' },
  { key: 'L5', label: 'L5 报价' },
  { key: 'L6', label: 'L6 逼单' },
  { key: 'L7', label: 'L7 成交' },
  { key: 'L8', label: 'L8 服务' },
];

export function StageSopCard({
  mode = 'compact',
  initialStage = 'L3',
  customerId,
}: {
  mode?: 'compact' | 'detail';
  initialStage?: string;
  customerId?: number;
}) {
  const [stage, setStage] = useState(initialStage);
  const { data } = useQuery<any>({
    queryKey: ['sales-sop', stage, customerId],
    queryFn: () => $.get('/sales-sop', { stage, customerId }),
  });
  const sop = data?.sop;
  const tailored: string[] = data?.tailored ?? [];

  return (
    <HomeCard
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span>{mode === 'detail' ? `阶段操作指南：${sop?.title ?? stage}` : '阶段操作指南'}</span>
          <span style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 400 }}>
            {sop?.duration && `· ${sop.duration}周期`}
          </span>
        </div>
      }
    >
      {/* stage 切换 chip */}
      <div style={{ padding: '8px 16px 4px', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {STAGES.map((s) => (
          <span
            key={s.key}
            onClick={() => setStage(s.key)}
            style={{
              padding: '3px 10px', fontSize: 12, borderRadius: 12, cursor: 'pointer',
              background: stage === s.key ? 'var(--purple)' : 'var(--bg-2)',
              color: stage === s.key ? '#fff' : 'var(--text-2)',
              border: '1px solid ' + (stage === s.key ? 'var(--purple)' : 'var(--border)'),
            }}
          >
            {s.label}
          </span>
        ))}
      </div>

      {sop && (
        <div style={{ padding: '8px 16px 14px', fontSize: 13, lineHeight: 1.6 }}>
          {/* 目标 + 退出条件 */}
          <div style={{ marginBottom: 10 }}>
            <span style={{ color: 'var(--text-3)', fontSize: 12 }}>🎯 目标：</span>{sop.goal}
            {sop.exit && (
              <span style={{ marginLeft: 12, fontSize: 12, color: 'var(--text-3)' }}>
                · 退出条件：{sop.exit}
              </span>
            )}
          </div>

          {/* tailored 个性化提示（仅 detail 模式 + 有 customerId 时显示） */}
          {tailored.length > 0 && (
            <div style={{
              padding: '8px 12px', marginBottom: 12,
              background: 'rgba(124,77,255,0.06)', borderLeft: '3px solid var(--purple)',
              borderRadius: 4, fontSize: 12,
            }}>
              {tailored.map((t: string, i: number) => (
                <div key={i} style={{ marginBottom: i < tailored.length - 1 ? 4 : 0 }}>
                  {t}
                </div>
              ))}
            </div>
          )}

          {/* DO / DON'T 两列 */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--success)', fontWeight: 500, marginBottom: 4 }}>
                ✅ 该做
              </div>
              {(sop.do ?? []).map((d: string, i: number) => (
                <div key={i} style={{ fontSize: 12, marginBottom: 3, color: 'var(--text-2)' }}>· {d}</div>
              ))}
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--danger)', fontWeight: 500, marginBottom: 4 }}>
                ❌ 不该做
              </div>
              {(sop.dont ?? []).map((d: string, i: number) => (
                <div key={i} style={{ fontSize: 12, marginBottom: 3, color: 'var(--text-2)' }}>· {d}</div>
              ))}
            </div>
          </div>

          {/* 关键话术（compact 模式只显示前 1 条，detail 全部）*/}
          {sop.scripts && sop.scripts.length > 0 && (
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 500, marginBottom: 6 }}>
                关键话术（点击复制）
              </div>
              {(mode === 'detail' ? sop.scripts : sop.scripts.slice(0, 1)).map((s: any, i: number) => (
                <ScriptLine key={i} situation={s.situation} line={s.line} />
              ))}
              {mode === 'compact' && sop.scripts.length > 1 && (
                <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
                  还有 {sop.scripts.length - 1} 条话术 · 进客户详情查看
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </HomeCard>
  );
}

function ScriptLine({ situation, line }: { situation: string; line: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{
      padding: '8px 10px', background: 'var(--bg-2)', borderRadius: 4,
      borderLeft: '3px solid var(--success)', marginBottom: 6, cursor: 'pointer',
    }}
      onClick={() => {
        navigator.clipboard.writeText(line).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <Pill tone="success">{situation}</Pill>
        <span style={{ fontSize: 11, color: copied ? 'var(--success)' : 'var(--text-3)', marginLeft: 'auto' }}>
          {copied ? '✓ 已复制' : '📋 点击复制'}
        </span>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-1)' }}>{line}</div>
    </div>
  );
}
